/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { ArrayExtensions, ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IProductSpecificationViewProps, ITabsData } from './dyn-product-specification';

interface IProductSpecificationTabsProps {
    tabsData: ITabsData;
    className?: string;
    config: IProductSpecificationViewProps['config'];
}

const ProductSpecificationTabs: React.FC<IProductSpecificationTabsProps> = ({ tabsData, className, config }) => {
    const [activeTab, setActiveTab] = React.useState(0);

    const handleKeyDown = React.useCallback(
        (event: React.KeyboardEvent) => {
            switch (event.key) {
                case 'ArrowLeft':
                    event.preventDefault();
                    setActiveTab(prev => (prev === 0 ? tabsData.tabs.length - 1 : prev - 1));
                    break;
                case 'ArrowRight':
                    event.preventDefault();
                    setActiveTab(prev => (prev === tabsData.tabs.length - 1 ? 0 : prev + 1));
                    break;
            }
        },
        [tabsData.tabs.length]
    );

    if (!tabsData.tabs || tabsData.tabs.length === 0) {
        return null;
    }

    return (
        <div className={`ms-product-specification__tabs ${className || ''}`}>
            <div className='ms-product-specification__tabs-header' role='tablist'>
                {tabsData.tabs.map((tab, index) => (
                    <button
                        key={tab.id}
                        className={`ms-product-specification__tab-button ${activeTab === index ? 'active' : ''}`}
                        onClick={() => setActiveTab(index)}
                        onKeyDown={handleKeyDown}
                        role='tab'
                        aria-selected={activeTab === index}
                        tabIndex={activeTab === index ? 0 : -1}
                        type='button'
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            <div className='ms-product-specification__tabs-content'>
                {// eslint-disable-next-line security/detect-object-injection
                tabsData.tabs[activeTab] && (
                    <div
                        className={`ms-product-specification__tab-panel ${
                            tabsData.tabs[activeTab].isNutritionalTab ? 'panel-nutritional-value' : ''
                        } ${tabsData.tabs[activeTab].isProductDetailsTab ? 'panel-product-details' : ''} ${
                            tabsData.tabs[activeTab].isInstructionsTab ? 'panel-instructions' : ''
                        } ${tabsData.tabs[activeTab].isIngredientsTab ? 'panel-ingredients' : ''} ${
                            tabsData.tabs[activeTab].isOthersTab ? 'panel-others' : ''
                        }`}
                    >
                        {// Ajouter la div nutritional-header pour l'onglet nutrition
                        tabsData.tabs[activeTab].isNutritionalTab && (
                            <div className='nutritional-header'>
                                <span className='ms-product-specification__tab-item-name'>{config.nutritionalElementLabel}</span>
                                <div className='ms-product-specification__tab-item-value'>
                                    <span>{config.nutritionalValueLabel}</span>
                                </div>
                            </div>
                        )}

                        {/* Affichage des items pour tous les onglets sauf cas spéciaux ProductDetailsTab */}
                        {tabsData.tabs[activeTab].items
                            .filter(
                                item =>
                                    !(
                                        tabsData.tabs[activeTab].isProductDetailsTab &&
                                        (item.technicalName?.toUpperCase().startsWith('WBS_SN_') ||
                                            item.technicalName?.toUpperCase() === 'WBS_BRAND')
                                    )
                            )
                            .map((item, itemIndex) => {
                                // Normaliser le technicalName en classe CSS valide
                                const technicalClass = item.technicalName
                                    ? item.technicalName
                                          .toLowerCase()
                                          .replace(/\s+/g, '_')
                                          .replace(/[^a-z0-9_-]/g, '_')
                                          .replace(/_{2,}/g, '_')
                                          .replace(/^_|_$/g, '')
                                    : '';

                                const attributeClassName = `ms-product-specification__tab-item ${technicalClass}`;

                                return (
                                    <div key={itemIndex} className={attributeClassName}>
                                        {tabsData.tabs[activeTab].isNutritionalTab && (
                                            <>
                                                <span className='ms-product-specification__tab-item-name'>{item.name}</span>
                                                <div className='ms-product-specification__tab-item-value'>
                                                    {item.cellData} {item.unitOfMeasureSymbol ?? ''}
                                                </div>
                                            </>
                                        )}
                                        {!tabsData.tabs[activeTab].isNutritionalTab && (
                                            <div className='ms-product-specification__tab-item-value-no-label'>{item.cellData}</div>
                                        )}
                                    </div>
                                );
                            })}

                        {/* Cas spécial ProductDetailsTab : afficher Special needs puis WBS_BRAND */}
                        {tabsData.tabs[activeTab].isProductDetailsTab &&
                            (() => {
                                // Bloc Special needs
                                const specialNeedsItems = tabsData.tabs[activeTab].items.filter(item =>
                                    item.technicalName?.toUpperCase().startsWith('WBS_SN_')
                                );
                                // Bloc WBS_BRAND
                                const brandItem = tabsData.tabs[activeTab].items.find(
                                    item => item.technicalName?.toUpperCase() === 'WBS_BRAND'
                                );
                                return (
                                    <>
                                        {specialNeedsItems.filter(item => item.value?.toString().toUpperCase() !== 'NO').length > 0 && (
                                            <div className='ms-product-specification__tab-item'>
                                                <span className='ms-product-specification__tab-item-name'>
                                                    {config?.specialNeedsElementLabel ?? 'Special needs'}
                                                </span>
                                                <div className='ms-product-specification__tab-item-value'>
                                                    {specialNeedsItems
                                                        .filter(item => item.value.toString().toUpperCase() !== 'NO')
                                                        .map((item, idx) => (
                                                            <div key={idx}>{item.cellData}</div>
                                                        ))}
                                                </div>
                                            </div>
                                        )}
                                        {brandItem && (
                                            <div className='ms-product-specification__tab-item'>
                                                <span className='ms-product-specification__tab-item-name'>{brandItem.name}</span>
                                                <div className='ms-product-specification__tab-item-value'>{brandItem.cellData}</div>
                                            </div>
                                        )}
                                    </>
                                );
                            })()}
                    </div>
                )}
            </div>
        </div>
    );
};

const ProductSpecificationView: React.FC<IProductSpecificationViewProps> = props => {
    const {
        ProductSpecification,
        ProductSpecificationTableProps,
        ProductSpecificationTableBodyProps,
        productSpecificationResult,
        isTabs,
        tabsData
    } = props;
    const rowdata =
        productSpecificationResult &&
        ArrayExtensions.hasElements(productSpecificationResult) &&
        productSpecificationResult.filter(row => !ObjectExtensions.isNullOrUndefined(row));

    // Si mode tabs et données disponibles
    if (isTabs && tabsData && tabsData.tabs.length > 0) {
        return (
            <Module {...ProductSpecification}>
                {props.title}
                <ProductSpecificationTabs tabsData={tabsData} config={props.config} />
            </Module>
        );
    }

    return rowdata && ArrayExtensions.hasElements(rowdata) ? (
        <Module {...ProductSpecification}>
            {props.title}
            <Node {...ProductSpecificationTableProps}>
                <Node {...ProductSpecificationTableBodyProps}>{rowdata}</Node>
            </Node>
        </Module>
    ) : null;
};

export default ProductSpecificationView;
